import { useState } from "react";
import { useNavigate } from "react-router";
import type { RunProgress } from "../../types/shadow-run";
import { SEVERITY_TIER_ORDER } from "../../types/severity-tier";
import {
  formatNumber,
  formatPercentage,
  formatDuration,
  formatDateTime,
} from "../../utils/formatters";
import { getMatchRateColor, getMatchRateLevel } from "../../utils/colors";
import { useActivityLog } from "../../hooks/useActivityLog";
import { StatusDistributionBar } from "../StatusDistributionBar/StatusDistributionBar";
import { LiveCounter } from "../LiveCounter/LiveCounter";
import { CircuitBreakerIndicator } from "../CircuitBreakerIndicator/CircuitBreakerIndicator";
import { SeverityBadge } from "../SeverityBadge/SeverityBadge";
import { ActivityLog } from "../ActivityLog/ActivityLog";
import styles from "./StandardProgress.module.css";

export interface StandardProgressProps {
  progress: RunProgress;
  orgName?: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "var(--colours-warning-500, #f59e0b)",
  running: "var(--colours-primary-500, #3b82f6)",
  completed: "var(--colours-success-500, #22c55e)",
  failed: "var(--colours-danger-500, #ef4444)",
};

const QUALITY_LABELS: Record<string, string> = {
  critical: "Critical Match Rate",
  warning: "Low Match Rate",
};

export function StandardProgress({ progress, orgName }: StandardProgressProps) {
  const navigate = useNavigate();
  const [failedIdsExpanded, setFailedIdsExpanded] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const tiersWithData = SEVERITY_TIER_ORDER.filter((tier) =>
    progress.status_breakdown.some((b) => b.tier === tier && b.count > 0),
  );

  const hasNoProcessedData = progress.processed_packets === 0;
  const isActive =
    progress.status === "running" || progress.status === "pending";
  const activityEntries = useActivityLog(progress);

  const matchRateColors = getMatchRateColor(progress.match_rate);
  const matchRateLevel = getMatchRateLevel(progress.match_rate);
  const qualityLabel = QUALITY_LABELS[matchRateLevel];

  // Hide "Safe" CB when quality is warning/critical — it contradicts.
  // Always show ARMED/TRIPPED since those are notable system states.
  const showCircuitBreaker =
    !qualityLabel || progress.circuit_breaker_state !== "SAFE";

  return (
    <div className={styles.container} data-testid="standard-progress">
      {/* Status + circuit breaker + quality indicator */}
      <div className={styles.statusRow}>
        <span
          className={isActive ? styles.statusDotPulsing : styles.statusDot}
          style={{ backgroundColor: STATUS_COLORS[progress.status] }}
        />
        <span className={styles.statusLabel}>{progress.status}</span>
        {showCircuitBreaker && (
          <>
            <span className={styles.statusSeparator}>|</span>
            <CircuitBreakerIndicator state={progress.circuit_breaker_state} />
          </>
        )}
        {qualityLabel && !hasNoProcessedData && (
          <>
            <span className={styles.statusSeparator}>|</span>
            <span
              className={styles.qualityDot}
              style={{ backgroundColor: matchRateColors.color }}
            />
            <span
              className={styles.qualityLabel}
              style={{ color: matchRateColors.color }}
            >
              {qualityLabel}
            </span>
          </>
        )}
      </div>

      {/* Message banner */}
      {progress.message && (
        <div className={styles.messageBanner}>{progress.message}</div>
      )}

      {/* Hero metric — large colored text on white, small level badge */}
      <div className={styles.heroMetric} data-testid="hero-metric">
        {hasNoProcessedData ? (
          <>
            <span className={styles.heroValueNeutral}>--%</span>
            <span className={styles.heroLabel}>Match Rate</span>
          </>
        ) : (
          <>
            <div className={styles.heroRow}>
              <span
                className={styles.heroValue}
                style={{ color: matchRateColors.color }}
              >
                {formatPercentage(progress.match_rate)}
              </span>
              <span
                className={styles.heroLevel}
                style={{ backgroundColor: matchRateColors.color }}
              >
                {matchRateLevel}
              </span>
            </div>
            <span className={styles.heroLabel}>Match Rate</span>
            {progress.processing_duration_seconds != null && (
              <span className={styles.heroDuration}>
                {formatDuration(progress.processing_duration_seconds)}
              </span>
            )}
          </>
        )}
      </div>

      {/* Progress bar + assessment accent */}
      <div className={styles.progressArea}>
        <StatusDistributionBar
          breakdown={progress.status_breakdown}
          height={24}
          indeterminate={isActive && hasNoProcessedData}
        />
        {qualityLabel && !hasNoProcessedData && (
          <div
            className={styles.progressAccent}
            style={{ backgroundColor: matchRateColors.color }}
          />
        )}
        <span className={styles.packetCount}>
          {formatNumber(progress.processed_packets)} /{" "}
          {formatNumber(progress.total_packets)} packets
        </span>
      </div>

      {/* Stats — no container, just spacing */}
      <div className={styles.statsStrip}>
        <LiveCounter
          value={progress.processed_packets}
          label="Processed"
          size="small"
        />
        <LiveCounter
          value={progress.error_rate}
          label="Error Rate"
          formatter={formatPercentage}
          size="small"
        />
        <LiveCounter
          value={progress.completion_rate ?? 0}
          label="Completion Rate"
          formatter={formatPercentage}
          size="small"
        />
        {progress.status === "completed" && (
          <LiveCounter
            value={progress.completed_match_rate ?? 0}
            label="Completed Match Rate"
            formatter={formatPercentage}
            size="small"
          />
        )}
        {(progress.skipped_count ?? 0) > 0 && (
          <LiveCounter
            value={progress.skipped_count ?? 0}
            label="Skipped"
            size="small"
          />
        )}
        {(progress.dlq_count ?? 0) > 0 && (
          <LiveCounter
            value={progress.dlq_count ?? 0}
            label="DLQ"
            size="small"
          />
        )}
      </div>

      {/* Activity log */}
      <ActivityLog entries={activityEntries} defaultExpanded={isActive} />

      {/* Tier breakdown */}
      {tiersWithData.length > 0 && (
        <div className={styles.tierSection}>
          <h3 className={styles.sectionTitle}>Variance in Matched Packets</h3>
          <div className={styles.tierList}>
            {tiersWithData.map((tier) => {
              const breakdown = progress.status_breakdown.find(
                (b) => b.tier === tier,
              );
              if (!breakdown) return null;
              return (
                <div key={tier} className={styles.tierRow}>
                  <span className={styles.tierBadge}>
                    <SeverityBadge tier={tier} />
                  </span>
                  <span className={styles.tierCount}>
                    {formatNumber(breakdown.count)}
                  </span>
                  <span className={styles.tierPercentage}>
                    ({formatPercentage(breakdown.percentage)})
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Run Details — collapsible, default collapsed */}
      <div className={styles.detailsSection}>
        <button
          className={styles.collapseToggle}
          onClick={() => setDetailsExpanded(!detailsExpanded)}
          type="button"
        >
          Run Details
          <span className={styles.toggleIcon}>
            {detailsExpanded ? "\u25B2" : "\u25BC"}
          </span>
        </button>
        {detailsExpanded && (
          <dl className={styles.detailsList}>
            {progress.batch_id && (
              <>
                <dt className={styles.detailsLabel}>Run ID</dt>
                <dd className={styles.detailsValue}>{progress.batch_id}</dd>
              </>
            )}
            {progress.org_id && (
              <>
                <dt className={styles.detailsLabel}>Organisation</dt>
                <dd className={styles.detailsValue}>
                  {orgName ?? progress.org_id}
                </dd>
              </>
            )}
            {progress.processing_duration_seconds != null && (
              <>
                <dt className={styles.detailsLabel}>Duration</dt>
                <dd className={styles.detailsValue}>
                  {formatDuration(progress.processing_duration_seconds)}
                </dd>
              </>
            )}
            {progress.started_at && (
              <>
                <dt className={styles.detailsLabel}>Started</dt>
                <dd className={styles.detailsValue}>
                  {formatDateTime(progress.started_at)}
                </dd>
              </>
            )}
            {progress.completed_at && (
              <>
                <dt className={styles.detailsLabel}>Completed</dt>
                <dd className={styles.detailsValue}>
                  {formatDateTime(progress.completed_at)}
                </dd>
              </>
            )}
          </dl>
        )}
      </div>

      {/* CTA — View Batch Details when completed */}
      {progress.status === "completed" && progress.batch_id && (
        <button
          type="button"
          className={styles.batchDetailButton}
          onClick={() => navigate(`/shadow-runs/${progress.batch_id}`)}
        >
          View Run Details
        </button>
      )}

      {/* Failed workflow IDs */}
      {progress.failed_workflow_ids.length > 0 && (
        <div className={styles.failedSection} data-testid="failed-ids-section">
          <button
            className={styles.collapseToggle}
            onClick={() => setFailedIdsExpanded(!failedIdsExpanded)}
            type="button"
          >
            Failed Workflows ({progress.failed_workflow_ids.length})
            <span className={styles.toggleIcon}>
              {failedIdsExpanded ? "\u25B2" : "\u25BC"}
            </span>
          </button>
          {failedIdsExpanded && (
            <ul className={styles.failedIdList}>
              {progress.failed_workflow_ids.map((id) => (
                <li key={id} className={styles.failedIdItem}>
                  {id}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
